<template>
	<model-viewer
		:src="$store.state.activeModel"
		environment-image="neutral"
		camera-orbit="45deg 70deg 65%"
		min-camera-orbit="auto 0deg 180m"
		max-camera-orbit="auto 94deg 380m"
		animation-crossfade-duration="500"
		animation-control-mode="manual"
		interaction-prompt-style="basic"
		:interaction-prompt="modelPrompt"
		camera-controls
		:class="{ 'hotspots-visible': activePrimaryNavigation === 'keyFeatures' && activeSecondaryNavigation }"
		@progress="updateProgress"
		@load="onModelLoad"
		@camera-change="onCameraChange"
		v-html="$store.state.productConfig.hotspots"
	/>

	<div
		v-if="$store.state.debug"
		class="debug-info"
	>
		<div>
			Orbit: {{ cameraOrbit }}<br>
			Target: {{ cameraTarget }}
		</div>
		<div>
			Active animations:<br>
			<span v-for="(a, i) in $store.state.activeAnimations" :key="i">
				{{ a }}<br>
			</span>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import 'model-viewer'

export default {
	name: 'ModelView',
	props: {
		playing: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			modelPrompt: 'auto',
			/** @type {?HTMLElement} */
			modelViewer: null,
			/** @type {[Node]} */
			hotspots: [],
			/** @type {Object.<string, THREE.AnimationAction>} */
			modelActions: {},
			/** @type {number} */
			animationsInProgress: 0,

			/** @type {?string} */
			lastRequestedAnimation: null,
			/** @type {?string} */
			lastPlayedName: null,
			/** @type {?THREE.AnimationAction} */
			lastPlayedAction: null,
			/** @type {?string} */
			activePresetAction: null,
			/** @type {?string} */
			activePreset: null,

			/** @type {Object.<string, Texture>} */
			extraTextures: {},

			/** @type {Object.<string, boolean>} */
			userPartsVisibility: {},

			initialCameraOrbit: '',
			cameraOrbit: '',
			cameraTarget: ''
		}
	},
	computed: mapState({
		activePrimaryNavigation: state => state.navigation.activePrimary,
		activeSecondaryNavigation: state => state.navigation.activeSecondary,
		activeSubExplanation: state => state.explanationPaneSub,
		explanationPaneOpen: state => state.explanationPaneOpen,
		explanationPaneContent: state => state.explanationPaneContent,
		animationsList: state => state.productConfig.animations,
		modelLoadingProgress: state => state.modelLoadingProgress,
		productConfig: state => state.productConfig
	}),
	mounted() {
		this.$bus.on('requestModelAnimation', async ({ name }) => {
			console.log('Requested: ' + name)
			if ( this.animationsInProgress ) return

			const isPreset = this.animationsList[name]?.preset

			if (isPreset) {
				if (this.activePreset === name) {
					// reset when same preset position is clicked again
					this.resetModel()
				} else {
					this.positionHandlers().preset(name)
				}

			} else if (this.positionHandlers()[name]) {
				this.positionHandlers()[name]()
			}

			this.lastRequestedAnimation = name
		})

		this.$bus.on('requestModelAnimationsReset', ({ exclude = null } = {}) => {
			this.resetModel(exclude)
		})

		this.$bus.on('requestModelPartsVisibility', nodes => {
			nodes.forEach(node => {
				this.userPartsVisibility[node.name] = node.visible
				this.setModelNodeVisibility(node.name, node.visible)

				if (node.name === 'foot_support') {
					this.userPartsVisibility['push_handels'] = !node.visible
					this.setModelNodeVisibility('push_handels', !node.visible)
				}
				if (
					node.name === 'head_support' &&
					node.visible &&
					this.isAnimationActive('poles_rotation_down')
				) {
					this.setModelNodeVisibility('head_support', false)
				}
			})
		})

		this.$bus.on('requestModelPartsTexture', nodes => {
			nodes.forEach(node => { this.setModelNodeTexture(node.name, node.texture) })
		})

		this.$bus.on('requestCameraPosition', cameraPosition => {
			this.setCameraPosition(cameraPosition)
		})
	},
	methods: {
		onModelLoad(e) {
			const viewer = e.target
			const actions = {}

			// add all available animations to mixer and set reference for easy access
			for (const [name, animation] of Object.entries(this.animationsList)) {
				const animationTrack = viewer.scene.animationsByName.get(animation.track)

				if (animationTrack) {
					const action = viewer.animationMixer.clipAction(animationTrack)

					action.setLoop(viewer.availableLoopModes.once)
					action.clampWhenFinished = true
					action.isReversed = animation.reversed

					actions[name] = action
				}
			}

			// set up hotspots (positioning is done by modelviewer)
			viewer.querySelectorAll('button').forEach(hotspot => {
				hotspot.setAttribute('disabled', '')
				hotspot.addEventListener('click', () => this.onHotspotClick(hotspot))
				this.hotspots.push(hotspot)
			})

			this.modelActions = actions
			this.modelViewer = viewer
			this.initialCameraOrbit = viewer.cameraOrbit

			window.modelViewer = viewer
			window.modelActions = actions;

			// hide some objects by default
			this.$bus.emit('requestModelPartsVisibility', [
				{ name: 'foot_support', visible: false },
				{ name: 'head_support', visible: false }
			])
		},

		isAnimationActive(name) {
			const isActive = action => action.isReversed && action.timeScale === -1 ? action.time === 0 : action.time > 0

			if (typeof name === 'object') {
				return name.some(it => it in this.modelActions && isActive(this.modelActions[it]))
			} else {
				return name in this.modelActions && isActive(this.modelActions[name])
			}
		},

		positionHandlers() {
			return {
				'folding_of_siderails_down': () => {
					const reverse = this.isAnimationActive('folding_of_siderails_down')
					this.playAnimation('folding_of_siderails_down', { reverse, fadeIn: false })
				},
				'poles': async () => {
					if (this.isAnimationActive('poles_move_up')) {
						await this.playAnimation('poles_move_up', { reverse: true, fadeIn: false })
						this.fastForwardAnimation('poles_move_down')
						this.fadeAnimations('poles_move_up', 0)
						return
					}
					if (this.isAnimationActive('poles_move_down')) {
						this.fadeAnimations('poles_move_down', 0)
						await this.positionHandlers().poles_rotation_down()
						return
					}
					if (this.isAnimationActive('poles_rotation_down')) {
						return this.positionHandlers().poles_rotation_down()
					}

					await this.playAnimation('poles_move_up', { fadeIn: false })
				},
				'poles_rotation_down': async () => {
					this.setModelNodeVisibility('head_support', false)

					const reverse = this.isAnimationActive('poles_rotation_down')
					await this.playAnimation('poles_rotation_down', { reverse, fadeIn: false })

					if (reverse && this.userPartsVisibility['head_support'] !== false) {
						this.setModelNodeVisibility('head_support', true)
					}
				},
				'pushhandles_down': async () => {
					this.setModelNodeVisibility('foot_support', false)
					this.setModelNodeVisibility('push_handels', true)

					const reverse = this.isAnimationActive('pushhandles_down')
					await this.playAnimation('pushhandles_down', { reverse, fadeIn: false })

					if (reverse && this.userPartsVisibility['foot_support'] !== false) {
						this.setModelNodeVisibility('foot_support', true)
						this.setModelNodeVisibility('push_handels', false)
					}
				},
				'mobilift': () => {
					const reverse = this.isAnimationActive('mobilift')
					this.playAnimation('mobilift', { reverse, fadeIn: false })
				},
				'idrive': () => {
					const reverse = this.isAnimationActive('idrive')
					this.playAnimation('idrive', { reverse, fadeIn: false })
				},

				'backrest_up': () => {
					if (this.isAnimationActive('backrest60')) return

					if (this.isAnimationActive('backrest30')) {
						this.fadeAnimations('backrest30', 0)
						this.playAnimation('backrest60', { fadeIn: false })
						return
					}

					this.playAnimation('backrest30', { fadeIn: false })
				},
				'backrest_down': async () => {
					if (this.isAnimationActive('backrest60')) {
						await this.playAnimation('backrest60', { reverse: true, fadeIn: false })
						this.fastForwardAnimation('backrest30')
						this.fadeAnimations('backrest60', 0)
						return
					}
					if (this.isAnimationActive('backrest30')) {
						await this.fadeAnimations('backrest30', 1.2)
					}
				},

				'legs_up': () => {
					if (this.isAnimationActive('calf_rest_adjustment01')) return
					if (this.isAnimationActive('leg_rest_adjustment02')) {
						this.playAnimation('calf_rest_adjustment01', { weight: 5 })
						return
					}
					if (this.isAnimationActive('leg_rest_adjustment01')) {
						this.playAnimation('leg_rest_adjustment02', { fadeIn: false })
						this.fadeAnimations('leg_rest_adjustment01', 0)
						return
					}
					this.playAnimation('leg_rest_adjustment01', { fadeIn: false })
				},
				'legs_down': async () => {
					if (this.isAnimationActive('calf_rest_adjustment01')) {
						await this.fadeAnimations('calf_rest_adjustment01', 1.2)
						this.fastForwardAnimation('leg_rest_adjustment02')
						return
					}
					if (this.isAnimationActive(['calf_rest_adjustment01', 'calf_rest_adjustment01'])) {
						this.positionHandlers().calfrest_down()
						return
					}
					if (this.isAnimationActive('leg_rest_adjustment02')) {
						await this.playAnimation('leg_rest_adjustment02', { reverse: true, fadeIn: false })
						this.fastForwardAnimation('leg_rest_adjustment01')
						this.fadeAnimations('leg_rest_adjustment02', 0)
						return
					}
					if (this.isAnimationActive('leg_rest_adjustment01')) {
						this.fadeAnimations('leg_rest_adjustment01', 1.2)
					}
				},

				'autocontour_up': () => {
					this.positionHandlers().backrest_up()
					if (this.isAnimationActive('leg_rest_adjustment02')) return
					this.positionHandlers().legs_up()
				},
				'autocontour_down': () => {
					this.positionHandlers().backrest_down()
					this.positionHandlers().legs_down()
				},

				'trendelenburg': async () => {
					if (this.isAnimationActive(['trendelenburg', 'trendelenburg_to_max', 'trendelenburg_to_min'])) return

					if (this.isAnimationActive('height_adjustment_down')) {
						this.fadeAnimations('height_adjustment_down')
						await this.playAnimation('trendelenburg_to_min', { reverse: true, fadeIn: false })
						this.fastForwardAnimation('trendelenburg')
						this.fadeAnimations('trendelenburg_to_min', 0)
						return
					}

					await this.reverseIfActive('reversetrendelenburg')
					this.fadeAnimations('reversetrendelenburg', 0)
					this.playAnimation('trendelenburg')
				},
				'reverse_trendelenburg': async () => {
					if (this.isAnimationActive(['reversetrendelenburg', 'reversetrendelenburg_to_max', 'reversetrendelenburg_to_min'])) return

					if (this.isAnimationActive('height_adjustment_down')) {
						this.fadeAnimations('height_adjustment_down')
						await this.playAnimation('reversetrendelenburg_to_min', { reverse: true, fadeIn: false })
						this.fastForwardAnimation('reversetrendelenburg')
						this.fadeAnimations('reversetrendelenburg_to_min', 0)
						return
					}

					await this.reverseIfActive('trendelenburg')
					this.fadeAnimations('trendelenburg', 0)
					this.playAnimation('reversetrendelenburg')
				},

				'bed_height_down': async () => {
					if (this.isAnimationActive('height_adjustment_down')) return

					if (this.isAnimationActive('trendelenburg')) {
						await this.playAnimation('trendelenburg_to_min', { fadeIn: false })
						this.fadeAnimations(['trendelenburg', 'trendelenburg_to_min'], 0)
						this.fastForwardAnimation('height_adjustment_down')
						return
					}

					if (this.isAnimationActive('reversetrendelenburg')) {
						await this.playAnimation('reversetrendelenburg_to_min', { fadeIn: false })
						this.fadeAnimations(['reversetrendelenburg', 'reversetrendelenburg_to_min'], 0)
						this.fastForwardAnimation('height_adjustment_down')
						return
					}

					this.playAnimation('height_adjustment_down')
				},
				'bed_height_up': async () => {
					if (this.isAnimationActive('trendelenburg')) {
						await this.playAnimation('trendelenburg', { reverse: true, fadeIn: false })
						this.fadeAnimations('trendelenburg', 0)
						return
					}

					if (this.isAnimationActive('reversetrendelenburg')) {
						await this.playAnimation('reversetrendelenburg', { reverse: true, fadeIn: false })
						this.fadeAnimations('reversetrendelenburg', 0)
						return
					}

					await this.reverseIfActive('height_adjustment_down')
					this.fadeAnimations('height_adjustment_down', 0)
				},

				'cpr': async () => {
					this.positionHandlers().bed_height_down()
					for (let i = 0; i < 3; i++ ) {
						// doing this 3 times to reverse all possible legs positions
						await Promise.all([
							this.positionHandlers().backrest_down(),
							this.positionHandlers().legs_down()
						])
					}
				},
			}
		},

		handleMattressVisibility() {
			const subExplanations = this.explanationPaneContent?.subExplanations || []
			const shouldHide = subExplanations.includes('ergoframe')
			if (shouldHide) {
				this.setModelNodeVisibility('matrace', false)
			} else if (this.userPartsVisibility['matrace'] !== false) {
				this.setModelNodeVisibility('matrace', true)
			}
		},

		async playAnimation(name, { reverse = false, blendFrom = '', speed = 1, fadeIn = true, start = 0, weight = 1 } = {}) {
			console.log(`${reverse ? 'Reversing' : 'Playing'} animation: ${name}${blendFrom ? ', blending from ' + blendFrom : ''}`)

			const action = this.modelActions[name]

			if (!action) return

			const timeScaleSign = reverse ? -1 : 1
			const duration = action.getClip().duration / speed

			// start the rendering loop
			this.modelViewer.play()

			if (!reverse) {
				action.reset()
				action.time = start
			} else {
				action.time = action.getClip().duration - start
			}

			if (blendFrom) {
				action.crossFadeFrom(this.modelActions[blendFrom], duration)
			} else {
				action.setEffectiveTimeScale(speed * timeScaleSign)
				action.setEffectiveWeight(weight)
				action.enabled = true
				action.paused = false
			}

			if (fadeIn) action.fadeIn(1)
			action.play()

			this.lastPlayedName = name
			this.lastPlayedAction = action
			this.animationsInProgress++

			if (blendFrom) {
				await new Promise(res => setTimeout(() => {
					this.modelActions[blendFrom].time = 0
					res()
				}, duration * 1000))
			} else {
				await this.onAnimationFinished(name)
			}

			// save resources by pausing the rendering loop when no animations are playing
			// if (this.animationsInProgress === 1) this.modelViewer.pause()
			this.animationsInProgress--
			console.log(`${reverse ? 'Reversing' : 'Animation'} ${name} finished`)

			if (this.$store.state.debug) this.activeAnimationsDebug()

			return
		},

		onAnimationFinished(name) {
			const action = this.modelActions[name]
			return new Promise(resolve => {
				// set up listener when animation finishes
				this.modelViewer.animationMixer.addEventListener('finished', action.finishedCallback = e => {
					const finishedTrackName = e.action.getClip().name

					if (finishedTrackName === this.animationsList[name].track) {
						this.modelViewer.animationMixer.removeEventListener('finished', action.finishedCallback)
						resolve()
					}
				})
			})
		},

		fastForwardAnimation(name) {
			return name in this.modelActions && this.playAnimation(name, { start: this.modelActions[name].getClip().duration, fadeIn: false })
		},

		reverseIfActive(animationNames) {
			const _animationNames = typeof animationNames === 'string' ? [animationNames] : animationNames
			const promises = []

			_animationNames.forEach(name => {
				if ( name in this.modelActions && this.isAnimationActive(name) ) {
					promises.push( this.playAnimation(name, { reverse: true, fadeIn: false }) )
				}
			})

			return Promise.all(promises)
		},

		async fadeAnimations(names, time = 1) {
			const _names = typeof names === 'string' ? [names] : names
			this.modelViewer.play()

			const promises = []
			_names.forEach(name => {
				console.log('Fading action: ' + name)
				const action = this.modelActions[name]

				if (!action) return

				const duration = time === 'auto' ? action.getClip().duration : time

				promises.push(new Promise(resolve => {
					// if (action.time === 0 && action.getEffectiveWeight() > 0) {
					// 	resolve()
					// 	return
					// }
					action.fadeOut(duration)
					this.animationsInProgress++

					setTimeout(() => {
						action.time = 0
						action.timeScale = 1
						this.animationsInProgress--
						resolve()
					}, duration * 1000)
				}))
			})

			return Promise.all(promises).then(() => {
				this.lastPlayedName = null
				this.lastPlayedAction = null
				this.lastRequestedAnimation = null

				if (this.$store.state.debug) this.activeAnimationsDebug()
				// save resources by pausing the rendering loop when no animations are playing
				// if (this.animationsInProgress === 0) this.modelViewer.pause()
			})
		},

		async resetModel(exclude) {
			console.log('Resetting all animations on model')

			this.activePreset = null
			this.activePresetAction = null

			const toFade = exclude ? this.$deleteFromArray(Object.keys(this.modelActions), exclude) : Object.keys(this.modelActions)

			await this.fadeAnimations(toFade, 1)
		},

		setModelNodeVisibility(nodeName, visibility) {
			const node = this.modelViewer.model.getNodeByName(nodeName)
			const object3d = this.modelViewer.scene.getChildByName(nodeName)

			this.modelViewer.play()

			if (node?.mesh) node.setMeshVisibility(visibility)
			if (object3d) object3d.visible = visibility

			setTimeout(() => {
				if (this.animationsInProgress === 0) this.modelViewer.pause()
			}, 100)
		},

		async setModelNodeTexture(nodeName, textureName) {
			const node = this.modelViewer.model.getNodeByName(nodeName)
			const baseColorTexture = node.getActiveMaterial()?.pbrMetallicRoughness.baseColorTexture

			const texturePath = `models/${this.$store.state.activeProduct}/TEX/${textureName}`
			const newTexture = textureName ? this.extraTextures[textureName] || await this.modelViewer.createTexture(texturePath) : null

			if (baseColorTexture) {
				baseColorTexture.setTexture(newTexture)
			}

			this.extraTextures[textureName] = newTexture
		},

		updateProgress(e) {
			this.$store.commit('setModelLoadingProgress', e.detail.totalProgress)
		},

		hideInteractionPrompt() {
			this.modelPrompt = 'none'
		},

		setCameraPosition(position) {
			this.modelViewer.cameraOrbit = position.orbit === 'initial' ? this.initialCameraOrbit : position.orbit
			this.modelViewer.cameraTarget = position.target === 'initial' ? '0 0 0' : position.target
			this.modelViewer.fieldOfView = position.fov || 'auto'
		},

		resetCameraPosition() {
			this.setCameraPosition({
				orbit: this.initialCameraOrbit,
				target: 'auto auto auto',
				fov: 'auto'
			})
		},

		onHotspotClick(hotspot) {
			const explanations = hotspot.dataset.explanations?.split('.')
			if (explanations) {
				const explanation = explanations[0]
				const subExplanation = explanations[1] || null
				const explanationItem = this.productConfig.explanations[explanation]
				const subExplanationItem = this.productConfig.explanations[subExplanation]
				this.$store.commit('navigation/setActiveSecondary', 'key_features.' + explanation)
				this.$store.commit('setExplanationPaneContent', explanationItem)
				this.$store.commit('setExplanationPaneSub', subExplanation)
				this.$store.commit('setExplanationPaneOpen', true)

				const camera = subExplanationItem?.camera || explanationItem.camera
				if (camera) {
					this.setCameraPosition(camera)
				}
			}
		},

		onCameraChange() {
			this.cameraOrbit = this.modelViewer?.getCameraOrbit() || ''
			this.cameraTarget = this.modelViewer?.getCameraTarget() || ''
		},

		activeAnimationsDebug() {
			const activeActions = Object.keys(this.modelActions).filter(it => this.isAnimationActive(it))
			this.$store.commit('setActiveAnimations', activeActions)
		}
	},
	watch: {
		activeSecondaryNavigation(val) {
			if (this.activePrimaryNavigation === 'keyFeatures') {
				// disable hotspots not related to active feature
				this.hotspots.forEach(hotspot => {
					const navItemName = val.split('.')[1]
					const hotspotExplanations = hotspot.dataset.explanations?.split('.')
					hotspot.classList.remove('active')
					if (hotspotExplanations[0] === navItemName) {
						if (hotspotExplanations.length === 1) {
							hotspot.classList.add('active')
						}
						hotspot.removeAttribute('disabled')
					} else {
						hotspot.setAttribute('disabled', '')
					}
				})
			}
		},
		activeSubExplanation(name) {
			if (this.activePrimaryNavigation === 'keyFeatures') {
				// display hotspot as active when related feature + subExplanation is selected
				this.hotspots.forEach(hotspot => {
					const hotspotExplanation = hotspot.dataset.explanations?.split('.')[1]
					if (hotspotExplanation === name) {
						hotspot.classList.add('active')
					} else {
						hotspot.classList.remove('active')
					}
				})
			}
			this.handleMattressVisibility()
		},
		explanationPaneOpen(open) {
			if (!open) {
				this.$nextTick(() => {
					this.resetCameraPosition()
				})
			}
			this.handleMattressVisibility()
		}
	},
}
</script>

<style lang="scss" scoped>
model-viewer {
	width: 100%;
	height: 100%;
	--poster-color: transparent;
	--min-hotspot-opacity: 0;

	&::part(default-progress-bar) {
		top: 30px;
		background-color: $clrRed;
	}
}

::v-deep(.Hotspot) {
	display: none;
	position: relative;
	width: 26px;
	height: 26px;
	background-color: transparentize($color: $clrWhite, $amount: 0.2);
	border-radius: 100px;
	box-shadow: 0 0 0 4px transparentize($color: $clrWhite, $amount: 0.5);
	transition: all 300ms ease 200ms;
	&.active,
	&:hover,
	&:disabled,
	&:not([data-visible]) {
		background-color: transparent;
		box-shadow: 0 0 0 0 transparent;
		transition: all 300ms ease;
	}
	&:disabled {
		pointer-events: none;
	}
	.hotspots-visible & {
		display: block;
	}

	&::before {
		content: "";
		position: absolute;
		width: 17px;
		height: 17px;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		border-radius: 100px;
		border: 1px solid $clrWhite;
		background-color: $clrRed;
		background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M128 29.799v196.403M226.202 128H29.799' stroke-linecap='round' stroke-miterlimit='10' stroke-width='51.7px' fill='none' stroke='%23FFFFFF'/%3E%3C/svg%3E%0A");
		background-size: 10px;
		background-repeat: no-repeat;
		background-position: center;
		transition: all 300ms ease 200ms;
	}
	&.active::before,
	&:hover::before,
	&:disabled::before,
	&:not([data-visible])::before {
		transform: translate(-50%, -50%) scale(0.6);
		opacity: 0;
		transition: all 300ms ease;
	}

	.HotspotAnnotation {
		position: absolute;
		left: 50%;
		bottom: 24px;
		padding: 0.2em 0.75em;
		transform: translate(-50%, 30%);
		opacity: 0;
		font-size: 12px;
		font-weight: 500;
		white-space: nowrap;
		border-radius: 100px;
		background-color: $clrWhite;
		transition: all 300ms ease;
		pointer-events: none;
		z-index: 100;

		&::before {
			content: "";
			position: absolute;
			width: 0;
			height: 0;
			bottom: -6px;
			left: calc(50% - 6px);
			border-style: solid;
			border-width: 6px 6px 0 6px;
			border-color: #ffffff transparent transparent transparent;
		}
	}
	&:hover, &.active {
		.HotspotAnnotation {
			opacity: 1;
			transform: translate(-50%, 0);
			pointer-events: all;
			color: $clrRed;
		}
	}
}

.debug-info {
	position: absolute;
	top: 50px;
	left: 340px;
	width: calc(100vw - 360px);
	font-size: 10px;
	display: flex;
	justify-content: space-between;
}
</style>
